/**
 * searchSession — owns the "reset all search state" invariant.
 *
 * vibeStore, SearchPage, and any future entry-point that starts a fresh
 * Search call resetSearchState() instead of manually resetting N stores.
 *
 * Registry pattern: each store REGISTERS its reset here at module init
 * (registerSearchReset), so this module never imports the stores — the old
 * version reached them with runtime require() calls to dodge circular
 * imports, which silently no-ops under an ESM bundler. Adding a store to
 * the search flow = one registerSearchReset call in that store's module.
 *
 * ADR-0001: apartmentStore.filters is the canonical active-Search state.
 * ADR-0007: Dossier-worthy signals (deal-breakers, work location, saves,
 *           notes) are preserved on reset — only transient Search criteria
 *           are cleared.
 */

type ResetFn = () => void;

const resets = new Map<string, ResetFn>();

/** Register a store's transient-Search reset. Idempotent per name (a store
 * module re-evaluating under HMR replaces its entry). */
export function registerSearchReset(name: string, fn: ResetFn): void {
  resets.set(name, fn);
}

/** Names currently registered — exposed for tests/debugging. */
export function registeredSearchResets(): string[] {
  return Array.from(resets.keys()).sort();
}

export function resetSearchState(): void {
  for (const [name, fn] of resets) {
    try {
      fn();
    } catch (e) {
      console.warn(`searchSession: reset "${name}" failed`, e);
    }
  }
}
